'use client';

import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, BarChart3, FileCode, Database } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

export default function PlotsAlotVsGammaPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              PlotsAlot vs Gamma
            </h1>
            <p className="text-xl text-muted-foreground mb-8">Gamma Alternative for Data Analysis & Visualization</p>
            <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
              Gamma is an AI-powered presentation tool. PlotsAlot is a specialized data analysis and visualization
              platform. Here's how to choose between them.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-6 py-3 rounded-lg border border-border hover:bg-accent transition-colors">
                See Pricing
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Key Differences Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Advanced Data Analysis</h3>
              <p className="text-muted-foreground text-sm">
                Execute Python code for statistical analysis, predictive modeling, and custom visualizations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                <FileCode className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Upload Any Data Format</h3>
              <p className="text-muted-foreground text-sm">
                Work with CSV, Excel, JSON, Parquet, and PDF files. No data transformation needed.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Chat with Your Data</h3>
              <p className="text-muted-foreground text-sm">
                Ask questions in natural language and get instant visualizations and insights.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Real Pricing Comparison</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Gamma Pricing */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Gamma</h3>
              <p className="text-3xl font-bold text-primary mb-4">$8-20/month</p>
              <p className="text-muted-foreground mb-6">
                Free: Limited presentations. Plus: $8/user/mo. Pro: $20/user/mo for teams.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Free tier</p>
                    <p className="text-sm text-muted-foreground">Limited presentations/month</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">AI presentation generation</p>
                    <p className="text-sm text-muted-foreground">Core feature</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Data analysis</p>
                    <p className="text-sm text-muted-foreground">Not designed for data work</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Python code execution</p>
                    <p className="text-sm text-muted-foreground">Presentation-focused only</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PlotsAlot Pricing */}
            <div className="bg-card border-2 border-primary rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                Best for Data Work
              </div>
              <h3 className="text-2xl font-bold mb-2">PlotsAlot</h3>
              <p className="text-3xl font-bold text-primary mb-4">$0-10/month</p>
              <p className="text-muted-foreground mb-6">
                Free tier available. Pro: $10/mo with 200 messages. Full data analysis included.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Free tier</p>
                    <p className="text-sm text-muted-foreground">3 messages/month</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Unlimited file uploads</p>
                    <p className="text-sm text-muted-foreground">CSV, Excel, JSON, PDF, Parquet</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Python code execution</p>
                    <p className="text-sm text-muted-foreground">Full data science stack</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Google Analytics integration</p>
                    <p className="text-sm text-muted-foreground">Direct data access</p>
                  </div>
                </div>
              </div>

              <Link
                href="/auth/signin"
                className="block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Full Feature Breakdown</h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">PlotsAlot</th>
                  <th className="text-center p-4 font-semibold">Gamma</th>
                  <th className="text-left p-4 font-semibold">Winner & Why</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">Data File Upload</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Supports CSV, Excel, JSON, PDF,
                    Parquet. Gamma doesn't handle data files.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Python Code Execution</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Full Python sandbox with pandas,
                    numpy, matplotlib, seaborn, plotly.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Statistical Analysis</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Regression, forecasting,
                    clustering, hypothesis testing available.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Custom Visualizations</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Any chart type via Python. Gamma
                    has basic charts for presentations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Natural Language Interface</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both use AI chat, but for different
                    purposes.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">AI-Powered Insights</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Partial</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - AI analyzes data patterns. Gamma
                    generates presentation content.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Presentation Creation</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Gamma</span> - Purpose-built for beautiful
                    presentations and slides.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Templates & Themes</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Gamma</span> - Extensive template library for
                    presentations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Google Analytics Integration</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Direct OAuth integration with GA4
                    for data analysis.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Export Options</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">PNG, Code</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">PDF, PPT</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Depends</span> - PlotsAlot for data/code, Gamma for
                    presentations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Collaboration</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Chat Sharing</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Gamma</span> - Advanced team collaboration for
                    presentations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Learning Curve</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Low</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Very Low</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Gamma</span> - More intuitive for presentation
                    creation.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">API Access</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Full REST API for integrations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Mobile Support</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both work on mobile browsers.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Understanding the Difference */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Understanding the Difference</h2>

            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-6">
                This comparison requires context:{' '}
                <strong className="text-foreground">Gamma and PlotsAlot serve fundamentally different purposes</strong>.
              </p>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <span className="text-purple-500 font-bold">G</span>
                  </div>
                  Gamma
                </h3>
                <p className="text-muted-foreground mb-0">
                  Gamma is an <strong className="text-foreground">AI-powered presentation creation tool</strong>. It
                  helps you create beautiful slides, documents, and webpages with AI assistance. Think of it as
                  "PowerPoint meets AI" - you describe what you want to present, and Gamma generates polished slides
                  with layouts, themes, and content suggestions.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg gradient-bg flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  PlotsAlot
                </h3>
                <p className="text-muted-foreground mb-0">
                  PlotsAlot is a <strong className="text-foreground">data analysis and visualization platform</strong>.
                  It helps you upload datasets, run statistical analysis, create custom visualizations, and execute
                  Python code. Think of it as "Jupyter Notebook meets ChatGPT" - you chat with your data and get
                  insights, charts, and analysis results.
                </p>
              </div>

              <p className="text-lg font-semibold text-foreground mt-8 mb-4">
                The key difference: Gamma creates presentations. PlotsAlot analyzes data.
              </p>
            </div>
          </div>
        </section>

        {/* When to Choose */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Choose PlotsAlot */}
            <div className="bg-card border-2 border-primary rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-primary" />
                When to Choose PlotsAlot
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You Need to Analyze Data</h4>
                  <p className="text-muted-foreground text-sm">
                    If your primary goal is understanding data patterns, running statistical analysis, or performing
                    predictive modeling, PlotsAlot is purpose-built for this. Upload your datasets and get instant
                    AI-powered insights.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. You Work with Large Datasets</h4>
                  <p className="text-muted-foreground text-sm">
                    PlotsAlot handles CSV files with millions of rows, Excel spreadsheets, JSON data, and even PDF
                    reports. It parses, cleans, and analyzes data efficiently with Python's data science stack.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. You Need Custom Visualizations</h4>
                  <p className="text-muted-foreground text-sm">
                    Create any chart type imaginable - from basic bar charts to complex heatmaps, violin plots, and
                    interactive dashboards. The Python sandbox gives you unlimited flexibility.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Want to Chat with Your Data</h4>
                  <p className="text-muted-foreground text-sm">
                    Ask questions like "What are the top 5 products by revenue?" or "Show me sales trends by region" and
                    get instant visualizations and answers. No SQL or coding required.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. You Need Google Analytics Integration</h4>
                  <p className="text-muted-foreground text-sm">
                    Connect your GA4 account directly and analyze website traffic data through natural language. Perfect
                    for marketers and product managers.
                  </p>
                </div>
              </div>
            </div>

            {/* Choose Gamma */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-purple-500" />
                When to Stick with Gamma
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You're Creating Presentations</h4>
                  <p className="text-muted-foreground text-sm">
                    If your goal is to create beautiful slides for meetings, pitches, or reports, Gamma excels at this.
                    It generates polished presentations with professional layouts and themes.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. You Want Templates & Themes</h4>
                  <p className="text-muted-foreground text-sm">
                    Gamma has an extensive template library for different presentation types. Perfect for quickly
                    creating visually appealing content without design skills.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. Team Collaboration is Priority</h4>
                  <p className="text-muted-foreground text-sm">
                    Gamma offers real-time collaboration features similar to Google Slides. Multiple team members can
                    work on presentations simultaneously.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Don't Work with Raw Data</h4>
                  <p className="text-muted-foreground text-sm">
                    If you're not analyzing datasets or running statistical analysis, Gamma's presentation focus makes
                    more sense than PlotsAlot's data-first approach.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. You Want Zero Learning Curve</h4>
                  <p className="text-muted-foreground text-sm">
                    Gamma is incredibly intuitive - just describe what you want to present and it generates slides.
                    Perfect for non-technical users who prioritize ease of use.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Case Scenarios */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Real-World Use Case Scenarios</h2>

            <div className="space-y-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Marketing Campaign Analysis</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Analyze CSV exports from ad platforms</li>
                      <li>Calculate ROI and conversion rates</li>
                      <li>Identify top-performing channels</li>
                      <li>Create custom attribution models</li>
                      <li>Forecast future performance</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-500 mb-2">Use Gamma if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Present results to stakeholders</li>
                      <li>Create campaign proposal slides</li>
                      <li>Design marketing strategy decks</li>
                      <li>Share insights in meetings</li>
                      <li>Build brand guideline presentations</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Sales Performance Review</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Upload sales data from CRM</li>
                      <li>Analyze trends and seasonality</li>
                      <li>Segment customers by behavior</li>
                      <li>Predict future sales numbers</li>
                      <li>Identify underperforming products</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-500 mb-2">Use Gamma if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Create quarterly review presentations</li>
                      <li>Design sales enablement materials</li>
                      <li>Present goals to sales team</li>
                      <li>Build pitch decks for prospects</li>
                      <li>Share success stories visually</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-3">Scenario: Product Analytics</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-primary mb-2">Use PlotsAlot if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Connect Google Analytics data</li>
                      <li>Analyze user behavior patterns</li>
                      <li>Calculate retention metrics</li>
                      <li>Perform cohort analysis</li>
                      <li>Create custom dashboard views</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-500 mb-2">Use Gamma if you need to:</p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>Present product roadmap</li>
                      <li>Create feature launch presentations</li>
                      <li>Design user research findings</li>
                      <li>Share product vision with team</li>
                      <li>Build stakeholder update decks</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Honest Limitations */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Honest Limitations</h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-primary">PlotsAlot Limitations</h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Not a presentation tool:</strong> PlotsAlot doesn't create
                      slides or formatted presentations. Export charts as images for use elsewhere.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Limited templates:</strong> No design templates or themes.
                      Focus is on data, not aesthetics.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Requires data files:</strong> You need actual datasets to
                      analyze. Can't generate mock data presentations.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Learning curve for advanced features:</strong> While chat is
                      easy, understanding statistical concepts helps get more value.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="text-xl font-semibold mb-4 text-purple-500">Gamma Limitations</h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">No data analysis:</strong> Gamma can't process datasets, run
                      statistics, or perform calculations on your data.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Limited chart customization:</strong> Basic chart options
                      only. No advanced visualizations like heatmaps or violin plots.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">No code execution:</strong> Can't run Python, perform
                      statistical tests, or create custom analysis workflows.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">Limited integrations:</strong> No Google Analytics, database,
                      or API connections for live data.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>

            <Accordion type="single" className="space-y-2">
              <AccordionItem value="item-1" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I use both Gamma and PlotsAlot together?</AccordionTrigger>
                <AccordionContent>
                  Yes! They complement each other well. Use PlotsAlot to analyze your data and create visualizations,
                  then export those charts to include in Gamma presentations. Many users analyze data in PlotsAlot
                  first, then use Gamma to present findings to stakeholders.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is better for non-technical users?</AccordionTrigger>
                <AccordionContent>
                  Both are accessible to non-technical users through natural language interfaces. Gamma is slightly
                  easier if you're creating presentations without data analysis. PlotsAlot is surprisingly easy for data
                  work - just upload files and ask questions in plain English. No coding knowledge required.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can Gamma analyze data like PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  No. Gamma is designed for creating presentations and documents, not data analysis. It can include
                  basic charts in presentations, but can't upload datasets, run statistical analysis, or execute Python
                  code like PlotsAlot.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can PlotsAlot create presentations like Gamma?</AccordionTrigger>
                <AccordionContent>
                  No. PlotsAlot focuses on data analysis and visualization. While it creates beautiful charts, it
                  doesn't generate full slide presentations with layouts, themes, and formatted content. You'd export
                  charts from PlotsAlot to use in your own presentations.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which has better AI capabilities?</AccordionTrigger>
                <AccordionContent>
                  Different AI strengths. Gamma's AI excels at generating presentation content, layouts, and design
                  suggestions. PlotsAlot's AI excels at understanding data patterns, recommending visualizations, and
                  providing statistical insights. Both use advanced language models.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>What file types does each support?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot supports data files: CSV, Excel (.xlsx, .xls), JSON, Parquet, and PDF (for text extraction).
                  Gamma supports document creation and can embed images and charts, but doesn't process raw data files.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is more cost-effective?</AccordionTrigger>
                <AccordionContent>
                  Depends on your needs. Gamma starts at $8/month for presentations. PlotsAlot starts at $19/month for
                  data analysis. If you need both capabilities, using both tools might be worthwhile. Both offer free
                  tiers to try before buying.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I migrate from Gamma to PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  These tools serve different purposes, so "migration" isn't quite the right concept. If you've been
                  using Gamma for presentations but now need data analysis capabilities, you can start using PlotsAlot
                  alongside Gamma. They're complementary, not competitive.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary/10 to-blue-500/10 rounded-2xl p-12 border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Data Analysis?</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free trial today. Upload your data and see insights in minutes.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-lg font-semibold">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-8 py-4 rounded-lg border-2 border-primary hover:bg-primary/5 transition-colors text-lg font-semibold">
                View Pricing
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              No credit card required • Free tier available • Chat with us directly
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="container mx-auto px-4 md:px-8 mt-16">
          <h3 className="text-2xl font-bold mb-6 text-center">Other Comparisons</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              href="/compare/chatgpt"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs ChatGPT</h4>
              <p className="text-sm text-muted-foreground">AI chat with specialized data analysis capabilities</p>
            </Link>
            <Link
              href="/compare/excel"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Excel</h4>
              <p className="text-sm text-muted-foreground">Traditional spreadsheets vs AI-powered analysis</p>
            </Link>
            <Link
              href="/compare/powerbi"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Power BI</h4>
              <p className="text-sm text-muted-foreground">Enterprise BI tools vs conversational analytics</p>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
